import { Component, OnInit, inject } from '@angular/core';
import { CompanyDashboardPageService } from './company-dashboard-page.service';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  IApplication,
  ICompany,
  IInterview,
  IJobOffer, IUser,
} from '../../../../../assets/types/dtoTypes';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../../shared/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ImageService } from '../../../../shared/services/image.service';
import { BIPPERZ_BREAKPOINTS, BreakpointsService } from '../../../../shared/services/breakpoint.service';
import { PATH } from '../../../../../assets/constants';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { Calendar, CalendarX2, ChevronRight, Clock } from 'lucide-angular';

interface IInterviewWithApp extends IInterview {
  applicationId: string;
}

interface CalendarDay {
  label: string;
  dateLabel: string;
  date: Date;
  interviews: IInterviewWithApp[];
}

@Component( {
    selector: 'app-company-dashboard-page-component',
  imports: [CommonModule, SharedModule, TranslateModule, RouterLink, ButtonComponent],
    templateUrl: './company-dashboard-page.component.html',
    styleUrl: './company-dashboard-page.component.scss',
    standalone: true,
} )
export class CompanyDashboardPageComponent implements OnInit {
  private router = inject(Router);
  private companyService = inject(CompanyDashboardPageService);
  private translateService = inject(TranslateService);
  private breakpointsService = inject(BreakpointsService);
  private imageService = inject(ImageService);
  private route = inject(ActivatedRoute);
  private authenticationService = inject(AuthenticationService);


  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
  protected readonly Calendar = Calendar;
  protected readonly CalendarX2 = CalendarX2;
  protected readonly Clock = Clock;
  protected readonly ChevronRight = ChevronRight;

  companyData?: ICompany;
  isLoading = true;
  jobOffers: IJobOffer[] = [];
  applications: IApplication[] = [];
  calendarDays: CalendarDay[] = [];
  isCalendarLoading = true;
  user: IUser;
  isMobile = false;


  constructor() {
    this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down ] ).subscribe( ( result ) => {
      this.isMobile = result.s || result.m_down;
    } );
  }

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.authenticationService.setAccessToken(token);
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
    }
    const companyId = this.route.snapshot.paramMap.get( 'id' );


    this.companyService.getCompanyData( companyId ).subscribe( {
      next: ( data ) => {
        this.companyData = data;
        this.imageService.appendSasTokenToCompanyImage( data.logoUrl, companyId, true ).subscribe( ( url ) => {
          this.companyData.logoUrl = url;
          this.companyService.getJobOffers().subscribe( {
            next: ( jobs: IJobOffer[] ) => {
              this.jobOffers = jobs;

              this.companyService.getApplications( companyId ).subscribe( {
                next: ( applications: IApplication[] ) => {
                  const imageUpdateTasks = applications.map( app =>
                    this.imageService.appendSasTokenToProfileImageUrl( app.applicantProfileImageUrl, app.userId ).toPromise().then( ( applicantUrl ) => {
                      app.applicantProfileImageUrl = applicantUrl;
                      return app;
                    } ),
                  );

                  Promise.all( imageUpdateTasks ).then( updatedApps => {
                    this.applications = updatedApps;
                    this.isLoading = false;

                    if ( updatedApps.length === 0 ) {
                      this.buildCalendarDays( [] );
                      this.isCalendarLoading = false;
                      return;
                    }
                    forkJoin( updatedApps.map( app =>
                      this.companyService.getInterviewsByApplication( app.id ).pipe(
                        map( interviews => interviews.map( iv => ( { ...iv, applicationId: app.id } ) ) )
                      )
                    ) ).subscribe( {
                      next: results => {
                        this.buildCalendarDays( results.flat() );
                        this.isCalendarLoading = false;
                      },
                      error: () => {
                        this.buildCalendarDays( [] );
                        this.isCalendarLoading = false;
                      },
                    } );
                  } );

                },
                error: ( err ) => {
                  console.error( 'Error fetching job offers', err );
                },
              } );
            },
            error: ( err ) => {
              console.error( 'Error fetching job offers', err );
            },
          } );

        } );
      },
      error: ( err ) => {
        console.error( 'Error fetching company data', err );
      },
    } );



  }

  createJobPost() {
    if ( this.companyData?.id ) {
      this.router.navigate( [ `/create-job-offer/${this.companyData.id}` ] );
    }
  }

  getTimeAgo( dateString: string ): string {
    const now = new Date();
    const createdDate = new Date( dateString );
    createdDate.setHours( createdDate.getHours() + 2 );
    const diffInSeconds = Math.floor( ( now.getTime() - createdDate.getTime() ) / 1000 );

    if ( diffInSeconds < 60 ) {
      return this.translateService.instant( 'timeAgo.justNow' );
    }

    const diffInMinutes = Math.floor( diffInSeconds / 60 );
    if ( diffInMinutes < 60 ) {
      return this.translateService.instant( 'timeAgo.minutesAgo', { count: diffInMinutes } );
    }

    const diffInHours = Math.floor( diffInMinutes / 60 );
    if ( diffInHours < 24 ) {
      return this.translateService.instant( 'timeAgo.hoursAgo', { count: diffInHours } );
    }

    const diffInDays = Math.floor( diffInHours / 24 );
    if ( diffInDays < 7 ) {
      return this.translateService.instant( 'timeAgo.daysAgo', { count: diffInDays } );
    }

    const diffInWeeks = Math.floor( diffInDays / 7 );
    if ( diffInWeeks < 4 ) {
      return this.translateService.instant( 'timeAgo.weeksAgo', { count: diffInWeeks } );
    }

    const diffInMonths = Math.floor( diffInDays / 30 );
    if ( diffInMonths < 12 ) {
      return this.translateService.instant( 'timeAgo.monthsAgo', { count: diffInMonths } );
    }

    const diffInYears = Math.floor( diffInDays / 365 );
    return this.translateService.instant( 'timeAgo.yearsAgo', { count: diffInYears } );
  }

  navigateToProfile( id: string ) {
    this.router.navigate( [ PATH.PROFIL, id ] );
  }

  navigateToInterview( applicationId: string ): void {
    this.router.navigate( [ '/application-overview', applicationId ] );
  }

  private buildCalendarDays( interviews: IInterviewWithApp[] ): void {
    const today = new Date();
    today.setHours( 0, 0, 0, 0 );

    this.calendarDays = Array.from( { length: 7 }, ( _, i ) => {
      const d = new Date( today );
      d.setDate( today.getDate() + i );

      const lang = this.translateService.currentLang || 'en';
      const label = i === 0 ? this.translateService.instant( 'dashboard.today' )
        : i === 1 ? this.translateService.instant( 'dashboard.tomorrow' )
        : d.toLocaleDateString( lang, { weekday: 'long' } );
      const dateLabel = d.toLocaleDateString( lang, { month: 'long', day: '2-digit' } );

      const dayInterviews = interviews.filter( iv => {
        const ivDate = new Date( iv.date );
        ivDate.setHours( 0, 0, 0, 0 );
        return ivDate.getTime() === d.getTime();
      } );

      return { label, dateLabel, date: d, interviews: dayInterviews };
    } );
  }

  getDuration( startTime: string, endTime: string ): string {
    const [ sh, sm ] = startTime.split( ':' ).map( Number );
    const [ eh, em ] = endTime.split( ':' ).map( Number );
    return `${ ( eh * 60 + em ) - ( sh * 60 + sm ) }m`;
  }

  formatTime( time: string ): string {
    const [ h, m ] = time.split( ':' ).map( Number );
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${ hour }:${ m.toString().padStart( 2, '0' ) } ${ period }`;
  }

  getInitials( name: string ): string {
    return name.split( ' ' ).map( p => p[ 0 ] ).join( '' ).toUpperCase().slice( 0, 2 );
  }

}
